<script setup>
import { ref } from 'vue';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Loader2 } from 'lucide-vue-next';

const props = defineProps({
  image: Object,
});

const emit = defineEmits(['remove']);
const isPopoverOpen = ref(false);
const selectedPoints = ref(props.image?.tags || []);

function removeImage() {
  emit('remove', props.image);
}

function toggleReferencePoint(point) {
  // Special handling for "all" option
  if (point === 'all') {
    if (selectedPoints.value.includes('all')) {
      // If "all" is already selected, deselect everything
      selectedPoints.value = [];
    } else {
      // If "all" is not selected, select all points
      selectedPoints.value = referencePoints.map((item) => item.id);
    }
  } else {
    const index = selectedPoints.value.indexOf(point);

    if (index === -1) {
      selectedPoints.value.push(point);
      // If all other points are selected, also select "all"
      const allPointsExceptAll = referencePoints
        .filter((item) => item.id !== 'all')
        .map((item) => item.id);
      if (allPointsExceptAll.every((id) => selectedPoints.value.includes(id))) {
        if (!selectedPoints.value.includes('all')) {
          selectedPoints.value.push('all');
        }
      }
    } else {
      selectedPoints.value.splice(index, 1);
      // If "all" was selected, deselect it when any point is deselected
      const allIndex = selectedPoints.value.indexOf('all');
      if (allIndex !== -1) {
        selectedPoints.value.splice(allIndex, 1);
      }
    }
  }

  props.image.tags = selectedPoints.value
    .map((id) => referencePoints.find((item) => id === item.id))
    .filter((item) => item)
    .map((item) => {
      return item.id;
    })
    .join(',');
}

function isPointSelected(point) {
  return selectedPoints.value.includes(point);
}

const referencePoints = [
  { id: 'all', label: 'attachTag.referencePoints.all' },
  { id: 'layout', label: 'attachTag.referencePoints.layout' },
  { id: 'style', label: 'attachTag.referencePoints.style' },
  { id: 'color', label: 'attachTag.referencePoints.color' },
  { id: 'texture', label: 'attachTag.referencePoints.texture' },
  { id: 'copy', label: 'attachTag.referencePoints.copy' },
  { id: 'font', label: 'attachTag.referencePoints.font' },
];
</script>
<template>
  <div
    v-if="image"
    class="group relative inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white transition-all duration-300 shrink-0"
  >
    <Popover v-model:open="isPopoverOpen">
      <PopoverTrigger as-child>
        <div class="flex items-center gap-2 cursor-pointer select-none">
          <!-- Image preview -->
          <div class="relative">
            <img
              v-if="!image.loading"
              :src="image.dataURL || image.url || image"
              class="w-6 h-6 object-cover rounded-lg shadow-sm ring-1 ring-white/50"
              alt="Reference image"
            />
            <Loader2 v-else class="w-6 h-6 animate-spin text-slate-400" />
            <div
              v-if="!image.loading"
              class="absolute inset-0 rounded-lg bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"
            ></div>
          </div>

          <!-- Label -->
          <span class="text-slate-700 text-xs font-medium tracking-tight">
            {{ $t('attachTag.editReferenceFeatures') }}
          </span>

          <!-- Dropdown indicator -->
          <svg
            class="w-3.5 h-3.5 text-slate-400 transition-transform group-hover:text-slate-600"
            :class="{ 'rotate-180': isPopoverOpen }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </PopoverTrigger>

      <PopoverContent class="w-80 p-0 shadow-xl rounded-2xl border-0 bg-white/95 backdrop-blur-xl">
        <div class="p-5">
          <!-- Header -->
          <div class="flex gap-2 mb-4 items-center">
            <h3 class="text-slate-800 text-sm font-semibold tracking-tight">
              {{ $t('attachTag.selectReferencePoints') }}
            </h3>
            <p class="text-slate-500 text-xs mt-1 leading-relaxed">
              {{ $t('attachTag.multipleSelection') }}
            </p>
          </div>

          <!-- Tag selection grid -->
          <div class="grid grid-cols-2 gap-2">
            <button
              v-for="point in referencePoints"
              :key="point.id"
              :class="[
                'group/tag relative p-3 rounded-xl border transition-all duration-200 text-left',
                isPointSelected(point.id)
                  ? 'bg-gradient-to-br from-[#20f6da]/10 to-[#86EEBA]/10 border-[#20f6da]/30 shadow-sm'
                  : 'bg-white/60 border-slate-200/80 hover:border-[#20f6da]/20 hover:bg-slate-50/80',
              ]"
              @click="toggleReferencePoint(point.id)"
            >
              <!-- Selection indicator -->
              <div
                :class="[
                  'absolute top-2 right-2 w-2 h-2 rounded-full transition-all duration-200',
                  isPointSelected(point.id)
                    ? 'bg-[#20f6da] shadow-lg shadow-[#20f6da]/30'
                    : 'bg-slate-200 group-hover/tag:bg-slate-300',
                ]"
              ></div>

              <!-- Label -->
              <span
                :class="[
                  'text-xs font-medium transition-colors duration-200',
                  isPointSelected(point.id)
                    ? 'text-slate-800'
                    : 'text-slate-600 group-hover/tag:text-slate-700',
                ]"
              >
                {{ $t(point.label) }}
              </span>
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>

    <!-- Remove button -->
    <button
      @click="removeImage"
      class="absolute -top-1 -right-1 w-5 h-5 bg-slate-800/90 hover:bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 opacity-0 group-hover:opacity-100 hover:scale-110 z-20"
    >
      <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2.5"
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    </button>
  </div>
</template>
